import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

// Safe data helpers
const safeNum = (val, def = 0) => (typeof val === 'number' ? val : def);
const safeStr = (val, def = '-') => (val ? String(val) : def);

// Summary Card Component
function SummaryCard({ title, value, subtitle, color = "teal", trend }) {
  const colors = {
    teal: "bg-teal-50 border-teal-200 text-teal-700",
    green: "bg-green-50 border-green-200 text-green-700",
    amber: "bg-amber-50 border-amber-200 text-amber-700",
    red: "bg-red-50 border-red-200 text-red-700",
    blue: "bg-blue-50 border-blue-200 text-blue-700",
    slate: "bg-slate-50 border-slate-200 text-slate-700"
  };

  return (
    <div className={`rounded-xl border p-4 ${colors[color]}`}>
      <p className="text-sm opacity-75">{title}</p>
      <p className="text-2xl font-bold">{value}</p>
      {subtitle && <p className="text-xs opacity-60 mt-1">{subtitle}</p>}
      {trend !== undefined && (
        <p className={`text-xs mt-1 ${trend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
          {trend >= 0 ? '↑' : '↓'} {Math.abs(trend)}% vs last period
        </p>
      )}
    </div>
  );
}

// KPI Table Component
function KPITable({ data, columns }) {
  if (!data || data.length === 0) {
    return <p className="text-slate-500 text-center py-8">No data available</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 bg-slate-50">
            {columns.map((col, idx) => (
              <th key={idx} className={`p-3 text-left font-semibold text-slate-700 ${col.align === 'right' ? 'text-right' : ''}`}>
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, idx) => (
            <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50">
              {columns.map((col, cidx) => (
                <td key={cidx} className={`p-3 ${col.align === 'right' ? 'text-right' : ''}`}>
                  {col.render ? col.render(row[col.key], row) : safeStr(row[col.key])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function ReportingDashboardPage() {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [period, setPeriod] = useState('month');
  const [loading, setLoading] = useState(true);
  const [combined, setCombined] = useState(null);
  const [operations, setOperations] = useState(null);
  const [compliance, setCompliance] = useState(null);
  const [financial, setFinancial] = useState(null);
  const [contractorKpis, setContractorKpis] = useState(null);
  const [projects, setProjects] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchCombined = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/reports/dashboard/combined`, { headers });
      setCombined(res.data);
    } catch (err) {
      console.error('Failed to fetch combined dashboard', err);
    }
  }, [token]);

  const fetchOperations = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/reports/operations/summary?period=${period}`, { headers });
      setOperations(res.data);
    } catch (err) {
      console.error('Failed to fetch operations', err);
    }
  }, [token, period]);

  const fetchCompliance = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/reports/compliance/summary`, { headers });
      setCompliance(res.data);
    } catch (err) {
      console.error('Failed to fetch compliance', err);
    }
  }, [token]);

  const fetchFinancial = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/reports/financial/summary?period=${period}`, { headers });
      setFinancial(res.data);
    } catch (err) {
      console.error('Failed to fetch financial', err);
    }
  }, [token, period]);

  const fetchContractorKpis = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/reports/contractor/kpis`, { headers });
      setContractorKpis(res.data);
    } catch (err) {
      console.error('Failed to fetch contractor KPIs', err);
    }
  }, [token]);

  const fetchProjects = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/reports/projects/summary`, { headers });
      setProjects(res.data);
    } catch (err) {
      console.error('Failed to fetch projects', err);
    }
  }, [token]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchCombined(), fetchOperations(), fetchCompliance(), fetchFinancial(), fetchContractorKpis(), fetchProjects()]);
      setLoading(false);
    };
    loadData();
  }, [fetchCombined, fetchOperations, fetchCompliance, fetchFinancial, fetchContractorKpis, fetchProjects]);

  useEffect(() => {
    if (activeTab === 'operations') fetchOperations();
    if (activeTab === 'financial') fetchFinancial();
  }, [period, activeTab, fetchOperations, fetchFinancial]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-slate-800">Reporting Dashboard</h1>
              <p className="text-sm text-slate-500">Operations, Compliance & Financial KPIs</p>
            </div>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Period" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="week">This Week</SelectItem>
                <SelectItem value="month">This Month</SelectItem>
                <SelectItem value="quarter">This Quarter</SelectItem>
                <SelectItem value="year">This Year</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="operations">Operations</TabsTrigger>
            <TabsTrigger value="compliance">Compliance</TabsTrigger>
            <TabsTrigger value="financial">Financial</TabsTrigger>
            <TabsTrigger value="contractors">Contractors</TabsTrigger>
            <TabsTrigger value="projects">Projects</TabsTrigger>
          </TabsList>

          {/* OVERVIEW TAB */}
          <TabsContent value="overview" className="space-y-6">
            {/* Executive Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <SummaryCard 
                title="Active Jobs" 
                value={safeNum(combined?.operations?.active_jobs)} 
                subtitle={`${safeNum(combined?.operations?.total_jobs)} total`}
                color="blue" 
              />
              <SummaryCard 
                title="Completed This Month" 
                value={safeNum(combined?.operations?.completed_this_month)} 
                color="green" 
              />
              <SummaryCard 
                title="Overdue Jobs" 
                value={safeNum(combined?.operations?.overdue_jobs)} 
                color={safeNum(combined?.operations?.overdue_jobs) > 0 ? "red" : "green"}
              />
              <SummaryCard 
                title="Compliance Rate" 
                value={`${safeNum(combined?.compliance?.compliance_rate)}%`} 
                subtitle={`${safeNum(combined?.compliance?.expired_certificates)} expired`}
                color={safeNum(combined?.compliance?.compliance_rate) >= 90 ? "green" : "amber"}
              />
            </div>

            {/* Financial Overview */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <SummaryCard 
                title="Total Approved Value" 
                value={`£${safeNum(combined?.financial?.total_approved_value).toLocaleString()}`} 
                color="teal" 
              />
              <SummaryCard 
                title="This Month" 
                value={`£${safeNum(combined?.financial?.month_approved_value).toLocaleString()}`} 
                color="teal" 
              />
              <SummaryCard 
                title="Projects At Risk" 
                value={safeNum(combined?.projects?.at_risk)} 
                subtitle={`${safeNum(combined?.projects?.active)} active`}
                color={safeNum(combined?.projects?.at_risk) > 0 ? "amber" : "green"}
              />
            </div>
          </TabsContent>

          {/* OPERATIONS TAB */}
          <TabsContent value="operations" className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <SummaryCard title="Total Jobs" value={safeNum(operations?.summary?.total_jobs)} color="blue" />
              <SummaryCard title="Active Jobs" value={safeNum(operations?.summary?.active_jobs)} color="teal" />
              <SummaryCard title={`Created (${period})`} value={safeNum(operations?.summary?.created_this_period)} color="slate" />
              <SummaryCard title={`Completed (${period})`} value={safeNum(operations?.summary?.completed_this_period)} color="green" />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <SummaryCard title="Completion Rate" value={`${safeNum(operations?.summary?.completion_rate)}%`} color="green" />
              <SummaryCard title="Avg Days to Complete" value={safeNum(operations?.summary?.avg_days_to_complete)} color="slate" />
              <SummaryCard title="Avg Response (hrs)" value={safeNum(operations?.summary?.avg_response_hours)} color="slate" />
              <SummaryCard title="Overdue Response" value={safeNum(operations?.summary?.overdue_response)} color={safeNum(operations?.summary?.overdue_response) > 0 ? "red" : "green"} />
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Status Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(operations?.status_breakdown || {}).map(([status, count]) => (
                    <Badge key={status} variant="outline" className="text-sm">
                      {status}: {count}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* COMPLIANCE TAB */}
          <TabsContent value="compliance" className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <SummaryCard title="Total Assets" value={safeNum(compliance?.summary?.total_assets)} color="blue" />
              <SummaryCard title="Compliant" value={safeNum(compliance?.summary?.compliant)} color="green" />
              <SummaryCard title="Due Within 30 Days" value={safeNum(compliance?.summary?.due_within_30_days)} color="amber" />
              <SummaryCard title="Expired" value={safeNum(compliance?.summary?.expired)} color="red" />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <SummaryCard title="Compliance Rate" value={`${safeNum(compliance?.summary?.compliance_rate)}%`} color={safeNum(compliance?.summary?.compliance_rate) >= 90 ? "green" : "amber"} />
              <SummaryCard title="Total Cases" value={safeNum(compliance?.cases?.total)} color="slate" />
              <SummaryCard title="Overdue Cases" value={safeNum(compliance?.cases?.overdue)} color={safeNum(compliance?.cases?.overdue) > 0 ? "red" : "green"} />
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Expiring Soon</CardTitle>
              </CardHeader>
              <CardContent>
                <KPITable 
                  data={compliance?.expiring_soon || []}
                  columns={[
                    { key: 'asset_name', header: 'Asset' },
                    { key: 'certificate_type', header: 'Certificate' },
                    { key: 'site_name', header: 'Site' },
                    { key: 'next_due_date', header: 'Due Date' }
                  ]}
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* FINANCIAL TAB */}
          <TabsContent value="financial" className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <SummaryCard title="Total Approved" value={`£${safeNum(financial?.summary?.total_approved_value).toLocaleString()}`} color="teal" />
              <SummaryCard title={`Period Approved`} value={`£${safeNum(financial?.summary?.period_approved_value).toLocaleString()}`} color="teal" />
              <SummaryCard title="Total Quoted" value={`£${safeNum(financial?.summary?.total_quoted_value).toLocaleString()}`} color="blue" />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <SummaryCard title="Quotes Pending" value={safeNum(financial?.summary?.quotes_pending)} color="amber" />
              <SummaryCard title="Quotes Approved" value={safeNum(financial?.summary?.quotes_approved)} color="green" />
              <SummaryCard title="Avg Job Value" value={`£${safeNum(financial?.summary?.avg_job_value).toLocaleString()}`} color="slate" />
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Invoice Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <SummaryCard title="Contractor Invoices Raised" value={`£${safeNum(financial?.invoices?.contractor_invoices_raised).toLocaleString()}`} color="slate" />
                  <SummaryCard title="Contractor Invoices Paid" value={`£${safeNum(financial?.invoices?.contractor_invoices_paid).toLocaleString()}`} color="green" />
                  <SummaryCard title="Client Invoices Raised" value={`£${safeNum(financial?.invoices?.client_invoices_raised).toLocaleString()}`} color="slate" />
                  <SummaryCard title="Client Invoices Paid" value={`£${safeNum(financial?.invoices?.client_invoices_paid).toLocaleString()}`} color="green" />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* CONTRACTORS TAB */}
          <TabsContent value="contractors" className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <SummaryCard title="Total Contractors" value={safeNum(contractorKpis?.totals?.total_contractors)} color="blue" />
              <SummaryCard title="Total Jobs Assigned" value={safeNum(contractorKpis?.totals?.total_jobs)} color="teal" />
              <SummaryCard title="Avg Completion Rate" value={`${safeNum(contractorKpis?.totals?.avg_completion_rate)}%`} color="green" />
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Contractor Performance</CardTitle>
              </CardHeader>
              <CardContent>
                <KPITable 
                  data={contractorKpis?.contractors || []}
                  columns={[
                    { key: 'contractor_name', header: 'Contractor' },
                    { key: 'total_jobs', header: 'Jobs', align: 'right' },
                    { key: 'completed_jobs', header: 'Completed', align: 'right' },
                    { key: 'completion_rate', header: 'Completion %', align: 'right', render: (v) => `${safeNum(v)}%` },
                    { key: 'avg_response_hours', header: 'Avg Response (hrs)', align: 'right', render: (v) => v ? `${v}h` : '-' },
                    { key: 'avg_completion_days', header: 'Avg Days', align: 'right', render: (v) => v ? `${v}d` : '-' },
                    { key: 'doc_completion_rate', header: 'Docs %', align: 'right', render: (v) => `${safeNum(v)}%` }
                  ]}
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* PROJECTS TAB */}
          <TabsContent value="projects" className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <SummaryCard title="Total Projects" value={safeNum(projects?.summary?.total_projects)} color="blue" />
              <SummaryCard title="Active" value={safeNum(projects?.summary?.active)} color="teal" />
              <SummaryCard title="At Risk" value={safeNum(projects?.summary?.at_risk)} color={safeNum(projects?.summary?.at_risk) > 0 ? "amber" : "green"} />
              <SummaryCard title="Budget Variance" value={`£${safeNum(projects?.summary?.budget_variance).toLocaleString()}`} color={safeNum(projects?.summary?.budget_variance) < 0 ? "red" : "green"} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <CardHeader><CardTitle>RAG Status</CardTitle></CardHeader>
                <CardContent>
                  <div className="flex gap-4">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-full bg-red-500"></div>
                      <span>{safeNum(projects?.rag_breakdown?.red)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-full bg-amber-500"></div>
                      <span>{safeNum(projects?.rag_breakdown?.amber)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-full bg-green-500"></div>
                      <span>{safeNum(projects?.rag_breakdown?.green)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {projects?.at_risk_projects?.length > 0 && (
              <Card>
                <CardHeader><CardTitle className="text-amber-600">At Risk Projects</CardTitle></CardHeader>
                <CardContent>
                  <KPITable 
                    data={projects.at_risk_projects}
                    columns={[
                      { key: 'reference', header: 'Reference' },
                      { key: 'title', header: 'Title' },
                      { key: 'rag_status', header: 'RAG', render: (v) => (
                        <Badge variant="outline" className={v === 'Red' ? 'border-red-500 text-red-500' : 'border-amber-500 text-amber-500'}>
                          {v}
                        </Badge>
                      )},
                      { key: 'risk_reason', header: 'Risk Notes' }
                    ]}
                  />
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
